'use client';
import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './form.scss';
import { GetEnterpriseQueryResult } from '@/sanity.types';
import { LandingPortableText } from '@/components/landingPortableText/landingPortableText';
import { PortableTextBlock } from 'next-sanity';

interface FormProps {
  formBuilder?: NonNullable<GetEnterpriseQueryResult>['formBuilder'];
}

interface FormData {
  [key: string]: string | string[];
}

export default function FormComponent({ formBuilder }: FormProps) {
  const [formData, setFormData] = useState<FormData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [showCaptchaError, setShowCaptchaError] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Initialize formData with empty values based on formFields
  React.useEffect(() => {
    if (formBuilder?.formFields) {
      const initialData: FormData = {};
      formBuilder.formFields.forEach((field) => {
        if (field.inputType === 'checkbox') {
          initialData[field.fieldName] = [];
        } else {
          initialData[field.fieldName] = '';
        }
      });
      setFormData(initialData);
    }
  }, [formBuilder]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prevData) => ({
        ...prevData,
        [name]: checked
          ? [...((prevData[name] as string[]) || []), value]
          : ((prevData[name] as string[]) || []).filter(
              (item) => item !== value
            ),
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
    setShowCaptchaError(false);
  };

  const validateForm = () => {
    let hasValidationErrors = false;
    const validationErrors: Record<string, string> = {};

    if (!captchaValue) {
      setShowCaptchaError(true);
      hasValidationErrors = true;
      validationErrors['captcha'] = 'Please complete the CAPTCHA verification';
    }

    if (formBuilder?.formFields) {
      formBuilder.formFields.forEach((field) => {
        if (field.required) {
          if (field.inputType === 'checkbox') {
            const values = formData[field.fieldName] as string[];
            if (!Array.isArray(values) || values.length === 0) {
              hasValidationErrors = true;
              validationErrors[field.fieldName] =
                `Please select at least one ${field.fieldLabel} option`;
            }
          } else {
            const value = formData[field.fieldName] as string;
            if (!value || value.trim() === '') {
              hasValidationErrors = true;
              validationErrors[field.fieldName] =
                `${field.fieldLabel} is required`;

              const inputEl = document.querySelector(
                `[name="${field.fieldName}"]`
              ) as HTMLInputElement;
              if (inputEl) {
                inputEl.setCustomValidity(`${field.fieldLabel} is required`);
                inputEl.reportValidity();
              }
            }
          }
        }
      });
    }

    if (hasValidationErrors) {
      setSubmitError('Please fill in all required fields');
      console.log('Validation errors:', validationErrors);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiPayload =
        formBuilder?.formFields?.reduce<Record<string, string | string[]>>(
          (acc, field) => {
            if (field.inputType === 'checkbox') {
              acc[field.fieldName] =
                (formData[field.fieldName] as string[]) || [];
            } else {
              acc[field.fieldName] =
                (formData[field.fieldName] as string) || '';
            }
            return acc;
          },
          {}
        ) || {};

      const response = await fetch('/api/enterprise-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...apiPayload,
          apiEndpoint: formBuilder?.apiEndpoint,
          secondaryApiEndpoint: formBuilder?.secondaryApiEndpoint,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit form');
      }

      setSubmitSuccess(true);

      // Reset form and captcha
      if (formBuilder?.formFields) {
        const initialData: FormData = {};
        formBuilder.formFields.forEach((field) => {
          if (field.inputType === 'checkbox') {
            initialData[field.fieldName] = [];
          } else {
            initialData[field.fieldName] = '';
          }
        });
        setFormData(initialData);
      }

      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'An error occurred'
      );
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!formBuilder?.formFields?.length) {
    return <div>Form configuration not available</div>;
  }

  return (
    <form className="enterprise-form" onSubmit={handleSubmit}>
      {formBuilder?.formTitle && <h2>{formBuilder?.formTitle}</h2>}
      {formBuilder?.formDescription && (
        <div className="sub-description">
          <LandingPortableText
            value={formBuilder?.formDescription as PortableTextBlock[]}
          />
        </div>
      )}
      {formBuilder.formFields.map((field) => {
        if (field.inputType === 'checkbox') {
          return (
            <div className="form-item" key={field._key}>
              <label>
                {field.fieldLabel}
                {field.required && <span className="required-mark">*</span>}
              </label>
              <div
                className="form-group"
                data-required={field.required}
                data-field-name={field.fieldName}
              >
                {field.checkboxOptions?.map((option) => (
                  <label key={option._key}>
                    <input
                      type="checkbox"
                      name={field.fieldName}
                      value={option.value}
                      checked={
                        Array.isArray(formData[field.fieldName]) &&
                        (formData[field.fieldName] as string[]).includes(
                          option.value as string
                        )
                      }
                      onChange={handleChange}
                      required={
                        field.required &&
                        Array.isArray(formData[field.fieldName]) &&
                        (formData[field.fieldName] as string[]).length === 0
                      }
                      aria-required={field.required}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div className="form-item" key={field._key}>
            <label htmlFor={field.fieldName}>
              {field.fieldLabel}
              {field.required && <span className="required-mark">*</span>}
            </label>
            <input
              type={field.inputType}
              id={field.fieldName}
              name={field.fieldName}
              value={(formData[field.fieldName] as string) || ''}
              onChange={handleChange}
              required={field.required}
              aria-required={field.required}
              placeholder={field.placeholder || ''}
            />
          </div>
        );
      })}

      <div className="recaptcha-wrap">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={'6LdBcCEUAAAAABcH2WFGbc2ZfOyt60UapRX5EXOM'}
          onChange={handleCaptchaChange}
        />
        {showCaptchaError && (
          <div className="error-message">
            Please complete the CAPTCHA verification
          </div>
        )}
      </div>

      {submitError && <div className="error-message">{submitError}</div>}
      {submitSuccess && (
        <div className="success-message">
          Thank you for submitting! We&apos;ll be in touch soon.
        </div>
      )}

      <div className="btn-wrap">
        <button
          type="submit"
          className={`btn btn-primary ${isSubmitting ? 'submitting' : ''}`}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              <span>Sending...</span>
            </>
          ) : (
            'Send Message'
          )}
        </button>
      </div>
    </form>
  );
}
